import React, { useId, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, Field, Input, Popover } from '@repo/design-system';
import { ink, semanticColor, space, typeRole } from '@repo/design-tokens';
import { visuallyHidden } from '@repo/ui';

import type { GeocodeState } from './useAddressGeocode';
import {
  isLocated,
  looksLikePlaceholderOrigin,
  type TwinDetailsErrors,
  type TwinPosition,
} from './twinDetailsForm';

/**
 * *Find latitude & longitude* and *Enter coordinates* — ONE set of position controls, rendered by
 * the create route's position card, by the summary's Position and opening view card
 * (`twin-details-page-summary`, R6.4: lifted out of `TwinDetailsDerived`, never copied) and by
 * Position on map (`retire-edit-mode-position-write-path`, R1).
 *
 * Find shows only for an empty position or the old placeholder (`twin-details-page-address`, R5.2,
 * Maria 2026-10-25); the pair saves `entered` when it is whole, valid and changed (R5.3).
 *
 * Position on map adds three things, each off by default so Twin details is unchanged:
 * - `orientation`, a third field in the same group, so leaving the group saves both (R2);
 * - `open`, the fields always shown, with no *Enter coordinates* / *Done* toggle;
 * - `addressAction="find-or-reset"`: on a real position the action reads *Reset to address
 *   position* and asks first, in the design system's `Popover` with the anatomy of the timezone
 *   confirm (`SummaryTimezoneCard`); *Move* saves, *Cancel*, Escape or a press outside save nothing
 *   (R3, Rob's ruling).
 *
 * Twin details passes `addressAction="none"` (`onboarding-persistent-scene-twin-details-map`): no
 * *Find*, because saving the address looks it up. The lookup's outcome (*Searching…*, *Find*'s own
 * no-match sentence, *Found …*) is announced in one polite live region (`PositionLookupOutcome`).
 */

export interface PositionControlsProps {
  className?: string;
  saved: TwinPosition;
  coords: TwinPosition;
  errors: TwinDetailsErrors;
  onCoordChange: (field: keyof TwinPosition, value: string) => void;
  /** Save the typed pair if it is whole and changed. `false`: held (half a pair, out of range). */
  onCoordCommit: () => boolean;
  /** Put back the saved pair (Escape). */
  onCoordRestore: () => void;
  geocode: { state: GeocodeState; canSearch: boolean; disabledReason?: string };
  /** *Find*. Not used with `addressAction="none"`. */
  onFind?: () => void;
  /** A third field, in degrees. Only Position on map passes it (R2). */
  orientation?: { value: string; error?: string; onChange: (value: string) => void };
  /** Always show the fields, with no *Enter coordinates* toggle. */
  open?: boolean;
  /**
   * `find-when-unplaced` (the default): *Find* only while the twin has no real position.
   * `find-or-reset`: *Find* then, *Reset to address position* (behind a confirm) otherwise.
   * `none`: no action; the address save's lookup outcome only (Twin details).
   */
  addressAction?: 'find-when-unplaced' | 'find-or-reset' | 'none';
  /** *Move* in the Reset confirm. Required with `find-or-reset`. */
  onReset?: () => void;
}

/**
 * The coordinate step: one in the fifth decimal place of a degree, about a metre. It is a unit of
 * latitude, not a shared decision, so it lives here.
 */
const LAT_LNG_STEP = 0.00001;

/**
 * The address lookup's outcome, in one polite live region that is always present, so each change is
 * announced (`onboarding-persistent-scene-twin-details-map`, R3.4, criterion 14): *Searching…* while
 * it looks, *Find*'s own sentence when nothing matched or the lookup failed, *Found …* on a match.
 * Empty otherwise.
 */
const LookupOutcome: React.FC<{ state: GeocodeState; className?: string }> = ({
  state,
  className,
}) => (
  <p
    className={[className, state.status === 'missed' ? 'is-miss' : ''].filter(Boolean).join(' ')}
    role="status"
    aria-live="polite"
    // Empty, it takes no room but stays in the accessibility tree, so its first message is announced.
    style={state.status === 'idle' ? visuallyHidden : undefined}
  >
    {state.status === 'searching' && 'Searching…'}
    {state.status === 'missed' && state.message}
    {state.status === 'found' && (
      <>
        Found <strong>{state.query}</strong>.
      </>
    )}
  </p>
);

export const PositionLookupOutcome = styled(LookupOutcome)`
  margin: 0;
  font: ${typeRole.caption1.fontWeight} ${typeRole.caption1.fontSize} /
    ${typeRole.caption1.lineHeight} ${typeRole.caption1.fontFamily};
  color: ${ink[3]};

  &.is-miss {
    color: ${semanticColor.errorStrong};
  }
`;

export const RESET_TITLE = 'Move the twin to its address position?';
export const RESET_BODY =
  'The twin record and the twin viewer use the new position straight away. Its orientation is kept.';

const PositionControls: React.FC<PositionControlsProps> = ({
  className,
  saved,
  coords,
  errors,
  onCoordChange,
  onCoordCommit,
  onCoordRestore,
  geocode,
  onFind,
  orientation,
  open = false,
  addressAction = 'find-when-unplaced',
  onReset,
}) => {
  const [entering, setEntering] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const pairRef = useRef<HTMLDivElement>(null);
  const actionRef = useRef<HTMLButtonElement>(null);
  const moveRef = useRef<HTMLButtonElement>(null);
  const confirmId = useId();
  const unplaced = !isLocated(saved) || looksLikePlaceholderOrigin(saved);
  const resets = addressAction === 'find-or-reset' && !unplaced;
  const noAction = addressAction === 'none';
  const actionShown = !noAction && (unplaced || addressAction === 'find-or-reset');
  const searching = geocode.state.status === 'searching';
  const fieldsShown = open || entering;
  /*
   * On Position on map the action stays focusable while it searches (`aria-disabled`, not native
   * `disabled`): *Move* closes the confirm and starts the search in one press, and the Popover hands
   * focus back to the action, which a disabled button would refuse, dropping a keyboard user's
   * focus to the page (review, Frontend & Accessibility).
   */
  const keepsFocus = addressAction === 'find-or-reset';

  const onAction = () => {
    if (searching) return;
    if (resets) setConfirming(true);
    else onFind?.();
  };
  const cancel = () => setConfirming(false);
  const move = () => {
    setConfirming(false);
    onReset?.();
  };

  const actionBlock = actionShown && (
    <div className="find">
      <span title={geocode.disabledReason}>
        <Button
          ref={actionRef}
          variant="frosted"
          disabled={!geocode.canSearch || (searching && !keepsFocus)}
          aria-disabled={(searching && keepsFocus) || undefined}
          onClick={onAction}
          {...(resets
            ? {
                'aria-haspopup': 'dialog' as const,
                'aria-expanded': confirming,
                'aria-controls': confirming ? confirmId : undefined,
              }
            : {})}
        >
          {searching
            ? 'Searching…'
            : resets
              ? 'Reset to address position'
              : 'Find latitude & longitude'}
        </Button>
      </span>
      {geocode.state.status === 'missed' && (
        <span className="reason is-miss" role="status">
          {geocode.state.message}
        </span>
      )}
      {geocode.state.status === 'idle' && geocode.disabledReason && (
        <span className="reason">{geocode.disabledReason}</span>
      )}
    </div>
  );
  const foundLine = geocode.state.status === 'found' && (
    <span className="reason" role="status">
      Found <strong>{geocode.state.query}</strong>.
    </span>
  );
  const fieldsBlock = fieldsShown ? (
    <div
      className={orientation ? 'pair with-orientation' : 'pair'}
      ref={pairRef}
      onBlur={event => {
        // Leaving the GROUP, not one field for another, is the moment to save (R5.3).
        if (!pairRef.current?.contains(event.relatedTarget as Node | null)) onCoordCommit();
      }}
      onKeyDown={event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          onCoordRestore();
          if (!open) setEntering(false);
        } else if (open && event.key === 'Enter') {
          event.preventDefault();
          onCoordCommit();
        }
      }}
    >
      <Field label="Latitude" helper="-90 to 90" error={errors.latitude}>
        {wiring => (
          <Input
            {...wiring}
            type="number"
            step={LAT_LNG_STEP}
            min={-90}
            max={90}
            autoFocus={!open}
            value={coords.latitude}
            onChange={event => onCoordChange('latitude', event.target.value)}
          />
        )}
      </Field>
      <Field label="Longitude" helper="-180 to 180" error={errors.longitude}>
        {wiring => (
          <Input
            {...wiring}
            type="number"
            step={LAT_LNG_STEP}
            min={-180}
            max={180}
            value={coords.longitude}
            onChange={event => onCoordChange('longitude', event.target.value)}
          />
        )}
      </Field>
      {orientation && (
        <Field label="Orientation" helper="-360 to 360" error={orientation.error}>
          {wiring => (
            <Input
              {...wiring}
              type="number"
              step={0.1}
              min={-360}
              max={360}
              value={orientation.value}
              onChange={event => orientation.onChange(event.target.value)}
            />
          )}
        </Field>
      )}
      {!open && (
        <div className="actions">
          <Button
            variant="text"
            size="inline"
            onClick={() => {
              if (onCoordCommit()) setEntering(false);
            }}
          >
            Done
          </Button>
        </div>
      )}
    </div>
  ) : (
    <div className="actions">
      <Button variant="text" size="inline" onClick={() => setEntering(true)}>
        Enter coordinates
      </Button>
    </div>
  );

  return (
    <div className={className}>
      {/* Always open (Position on map): the fields first, so Tab reaches them before the
          address action (criterion 13). Otherwise the action leads, as Twin details draws it. */}
      {noAction ? (
        <>
          <PositionLookupOutcome state={geocode.state} />
          {fieldsBlock}
        </>
      ) : (
        <>
          {open ? fieldsBlock : actionBlock}
          {open ? actionBlock : foundLine}
          {open ? foundLine : fieldsBlock}
        </>
      )}
      {addressAction === 'find-or-reset' && (
        <Popover
          id={confirmId}
          open={confirming}
          onClose={cancel}
          anchorRef={actionRef}
          title={RESET_TITLE}
          glyph="triangle-exclamation"
          glyphTone="butter"
          initialFocusRef={moveRef}
          actions={
            <>
              <Button ref={moveRef} variant="primary" onClick={move}>
                Move
              </Button>
              <Button variant="frosted" onClick={cancel}>
                Cancel
              </Button>
            </>
          }
        >
          {RESET_BODY}
        </Popover>
      )}
    </div>
  );
};

export default styled(PositionControls)`
  display: flex;
  flex-direction: column;
  gap: ${space[2]};

  .reason {
    margin: 0;
    font: ${typeRole.caption1.fontWeight} ${typeRole.caption1.fontSize} /
      ${typeRole.caption1.lineHeight} ${typeRole.caption1.fontFamily};
    color: ${ink[3]};
  }

  .reason.is-miss {
    color: ${semanticColor.errorStrong};
  }

  .find {
    display: flex;
    align-items: center;
    gap: ${space[3]};
    flex-wrap: wrap;
  }

  .find > span:first-child {
    width: 240px;
  }

  .pair {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${space[3]};
    align-items: start;
  }

  .pair.with-orientation {
    grid-template-columns: 1fr 1fr 1fr;
  }

  .pair .actions {
    grid-column: 1 / -1;
  }

  .actions {
    display: flex;
  }
`;
